import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage } from './ErrorMessage';
import { Hint } from './Hint';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy } from './cx';

export interface AutocompleteProps<T> {
  id: string;
  label: ReactNode;
  labelClassName?: string;
  hint?: ReactNode;
  error?: string;
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  /** Minimum characters before searching (default 2). */
  minChars?: number;
  /** Debounce of the search in ms (default 250). */
  debounceMs?: number;
  search: (query: string, signal: AbortSignal) => Promise<T[]>;
  getOptionKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  /** Secondary line of an option (e.g. IČO and municipality). */
  getOptionHint?: (option: T) => string | undefined;
  onSelect: (option: T) => void;
  /** Text shown in the input after a selection (default: the option label). Empty string clears the input. */
  selectionText?: (option: T) => string;
  /** Text of the input while the user has not typed (e.g. the label of the current value once it is loaded). */
  initialText?: string;
  /** Called when the user edits the text (not on a selection), e.g. to clear a value that no longer matches. */
  onTextChange?: (text: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  formGroupClassName?: string;
  labelLang?: string;
  hintLang?: string;
  /** Unavailable: stays focusable and announced (aria-disabled, readonly), no search. */
  ariaDisabled?: boolean;
  beforeInput?: ReactNode;
  extraDescribedBy?: string;
}

/**
 * Editable combobox with list autocomplete and manual selection (WAI-ARIA APG combobox pattern), styled with
 * ID-SK input and list styles. Keyboard: Down/Up move through the options, Home/End jump to the first/last option
 * while the list is open, Enter selects, Escape closes the list (or clears the text when it is closed).
 * The number of results is announced in a polite live region.
 */
export function Autocomplete<T>({
  id,
  label,
  labelClassName,
  hint,
  error,
  required,
  minChars = 2,
  debounceMs = 250,
  search,
  getOptionKey,
  getOptionLabel,
  getOptionHint,
  onSelect,
  selectionText,
  initialText = '',
  onTextChange,
  inputRef,
  formGroupClassName,
  labelLang,
  hintLang,
  ariaDisabled,
  beforeInput,
  extraDescribedBy,
}: AutocompleteProps<T>) {
  const { t } = useTranslation();
  const listboxId = `${id}-listbox`;
  const labelId = useId();
  const [text, setText] = useState(initialText);
  const [options, setOptions] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState('');
  const pending = useRef<AbortController | null>(null);
  /** Only text typed by the user triggers a search (not the initial text or a selection). */
  const typed = useRef(false);
  const toggle = useRef(false);

  useEffect(() => {
    if (!typed.current) setText(initialText);
  }, [initialText]);

  const announce = (message: string) => {
    toggle.current = !toggle.current;
    setStatus(message + (toggle.current ? ' ' : ''));
  };

  useEffect(() => {
    if (!typed.current) return;
    const query = text.trim();
    pending.current?.abort();
    if (query.length < minChars) return;
    const controller = new AbortController();
    pending.current = controller;
    const timer = window.setTimeout(() => {
      search(query, controller.signal)
        .then((results) => {
          if (controller.signal.aborted) return;
          setOptions(results);
          setActive(-1);
          setOpen(true);
          announce(results.length === 0 ? t('autocomplete.noResults') : t('autocomplete.results', { count: results.length }));
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setOptions([]);
            setOpen(false);
            announce(t('autocomplete.failed'));
          }
        });
    }, debounceMs);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- search/t are stable enough; re-run only on text changes
  }, [text]);

  const choose = (option: T) => {
    const next = selectionText ? selectionText(option) : getOptionLabel(option);
    typed.current = false;
    setText(next);
    setOpen(false);
    setActive(-1);
    setOptions([]);
    onSelect(option);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const count = options.length;
    switch (event.key) {
      case 'ArrowDown':
        if (count === 0) return;
        event.preventDefault();
        if (!open) {
          setOpen(true);
          setActive(0);
        } else {
          setActive((a) => (a + 1) % count);
        }
        break;
      case 'ArrowUp':
        if (!open || count === 0) return;
        event.preventDefault();
        setActive((a) => (a <= 0 ? count - 1 : a - 1));
        break;
      case 'Home':
        if (!open || count === 0) return;
        event.preventDefault();
        setActive(0);
        break;
      case 'End':
        if (!open || count === 0) return;
        event.preventDefault();
        setActive(count - 1);
        break;
      case 'Enter':
        if (open && active >= 0 && options[active]) {
          event.preventDefault();
          choose(options[active]);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
          setActive(-1);
        } else if (text && !ariaDisabled) {
          event.preventDefault();
          typed.current = true;
          setText('');
          setOptions([]);
          onTextChange?.('');
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
    }
  };

  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const activeId = open && active >= 0 ? `${id}-option-${active}` : undefined;
  const showList = open && options.length > 0;

  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <label className={cx('govuk-label', labelClassName)} htmlFor={id} id={labelId}>
        <span lang={labelLang}>{label}</span>
        <RequiredMarker required={required} />
      </label>
      {hint && (
        <Hint id={hintId} lang={hintLang}>
          {hint}
        </Hint>
      )}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      {beforeInput}
      <div className="app-autocomplete">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={cx('govuk-input', error && 'govuk-input--error')}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-activedescendant={activeId}
          aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
          aria-invalid={error ? true : undefined}
          aria-disabled={ariaDisabled ? true : undefined}
          readOnly={ariaDisabled}
          autoComplete="off"
          spellCheck={false}
          value={text}
          onChange={(e) => {
            if (ariaDisabled) return;
            typed.current = true;
            setText(e.target.value);
            onTextChange?.(e.target.value);
            if (e.target.value.trim().length < minChars) {
              setOptions([]);
              setOpen(false);
              setActive(-1);
            }
          }}
          onKeyDown={onKeyDown}
          onBlur={() => setOpen(false)}
        />
        <div className="app-autocomplete__listbox" id={listboxId} role="listbox" aria-labelledby={labelId} hidden={!showList}>
          {options.map((option, index) => (
            // Options are reached with the arrow keys from the combobox input (aria-activedescendant); a mouse click
            // selects, and mousedown is prevented so the input keeps focus.
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              key={getOptionKey(option)}
              id={`${id}-option-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === active}
              className={cx('app-autocomplete__option', index === active && 'app-autocomplete__option--focused')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(option)}
            >
              {getOptionLabel(option)}
              {getOptionHint?.(option) && <span className="app-autocomplete__option-hint">{getOptionHint(option)}</span>}
            </div>
          ))}
        </div>
      </div>
      <div className="govuk-visually-hidden" aria-live="polite" aria-atomic="true" data-testid={`${id}-status`}>
        {status}
      </div>
    </div>
  );
}
